import { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes, useNavigate, useParams } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Mic2, Music2, DollarSign, Shield,
  Activity, Download, Search, Loader2, LogOut, Globe, BarChart3, Flag, LifeBuoy, KeyRound, UserCog, Gift,
} from "lucide-react";
import AffiliatesAdmin from "@/components/admin/AffiliatesAdmin";
import PayoutsAdmin from "@/components/admin/PayoutsAdmin";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { toast } from "sonner";

const COLORS = ["#FF0050", "#8A2BE2", "#00F2FE", "#FFD60A", "#22c55e", "#ef4444", "#3b82f6", "#a855f7"];

export default function AdminApp() {
  const { user, booted, logout } = useAuth();
  const navigate = useNavigate();

  if (!booted) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="w-10 h-10 text-[#FF0050] animate-spin" /></div>;
  }
  if (!user) return <Navigate to="/auth" replace />;
  const isOwner = user.role === "admin";
  if (!isOwner && user.role !== "staff") return <Navigate to="/" replace />;
  // The owner can do everything; a team member only what the owner gave them (the server enforces it too).
  const can = (perm) => isOwner || (user.permissions || []).includes(perm);

  const allItems = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard", perm: "dashboard" },
    { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testid: "admin-nav-analytics", perm: "analytics" },
    { to: "/admin/users", label: "Utilisateurs", icon: Users, testid: "admin-nav-users", perm: "users" },
    { to: "/admin/artists", label: "Artistes", icon: Mic2, testid: "admin-nav-artists", perm: "users" },
    { to: "/admin/content", label: "Contenu", icon: Music2, testid: "admin-nav-content", perm: "content" },
    { to: "/admin/financial", label: "Finance", icon: DollarSign, testid: "admin-nav-financial", perm: "finance" },
    { to: "/admin/affiliates", label: "Affiliation", icon: Gift, testid: "admin-nav-affiliates", perm: "affiliation" },
    { to: "/admin/support", label: "Support", icon: LifeBuoy, testid: "admin-nav-support", perm: "support" },
    { to: "/admin/reports", label: "Signalements", icon: Flag, testid: "admin-nav-reports", perm: "reports" },
    { to: "/admin/security", label: "Sécurité", icon: Shield, testid: "admin-nav-security", perm: "security" },
    { to: "/admin/team", label: "Équipe", icon: UserCog, testid: "admin-nav-team", perm: "__owner" },
  ];
  const items = allItems.filter((it) => (it.perm === "__owner" ? isOwner : can(it.perm)));
  const firstAllowed = items[0]?.to;
  const guard = (perm, el) => (perm === "__owner" ? isOwner : can(perm)) ? el : (
    <div className="text-center text-zinc-500 py-16" data-testid="admin-no-access">
      <p>Tu n'as pas accès à cette page.</p>
      {firstAllowed && <button onClick={() => navigate(firstAllowed)} className="btn-secondary mt-4">Aller à une page autorisée</button>}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white flex" data-testid="admin-app">
      {/* Sidebar */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-white/5 bg-black/50 backdrop-blur-xl">
        <div className="px-5 py-5 border-b border-white/5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF0050] font-black">Admin Console</p>
          <h1 className="text-xl font-black font-display mt-1">Meta<span className="gradient-text">zik</span></h1>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition ${isActive ? "bg-white/10 text-white" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`}
            >
              <it.icon className="w-4 h-4" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/5 space-y-1">
          <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold text-zinc-400 hover:bg-white/5 hover:text-white transition" data-testid="admin-go-app">
            <Globe className="w-4 h-4" /> Retour à l'app
          </button>
          <button onClick={() => { logout(); navigate("/auth"); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold text-zinc-400 hover:bg-white/5 hover:text-red-400 transition" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Mobile top nav — used to only list the section links (Dashboard,
          Analytics...), with no way to log out or return to the normal app
          short of the browser's own back button. Same two actions as the
          desktop sidebar below, just appended to this scrollable row. */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center gap-1 overflow-x-auto scroll-hide px-3 py-2">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => `shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold ${isActive ? "bg-white text-black" : "bg-white/5 text-zinc-400"}`}>
              <it.icon className="w-3 h-3" /> {it.label}
            </NavLink>
          ))}
          <button
            onClick={() => navigate("/")}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-white/5 text-zinc-400"
            data-testid="admin-go-app-mobile"
          >
            <Globe className="w-3 h-3" /> App
          </button>
          <button
            onClick={() => { logout(); navigate("/auth"); }}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-white/5 text-red-400"
            data-testid="admin-logout-mobile"
          >
            <LogOut className="w-3 h-3" /> Déconnexion
          </button>
        </div>
      </div>

      <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-10 py-6 md:py-8 pt-16 md:pt-8">
        <Routes>
          <Route index element={guard("dashboard", <DashboardView />)} />
          <Route path="analytics" element={guard("analytics", <AnalyticsView />)} />
          <Route path="users" element={guard("users", <UsersView />)} />
          <Route path="artists" element={guard("users", <UsersView roleFilter="artist" />)} />
          <Route path="artists/:id" element={guard("users", <ArtistDetailView />)} />
          <Route path="content" element={guard("content", <ContentView />)} />
          <Route path="financial" element={guard("finance", <FinancialView />)} />
          <Route path="affiliates" element={guard("affiliation", <AffiliatesAdmin />)} />
          <Route path="support" element={guard("support", <SupportAdminView />)} />
          <Route path="reports" element={guard("reports", <ReportsView />)} />
          <Route path="security" element={guard("security", <SecurityView />)} />
          <Route path="team" element={guard("__owner", <TeamView />)} />
        </Routes>
      </main>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function StatCard({ label, value, sub, accent = "#FF0050", testid }) {
  return (
    <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10 hover:border-white/20 transition" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-black">{label}</p>
      <p className="text-3xl font-black font-display mt-2" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

function DashboardView() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ts, setTs] = useState(null);
  const [top, setTop] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [d, t, tt] = await Promise.all([
          api.get("/admin/dashboard"),
          api.get("/admin/analytics/timeseries", { params: { period: "month" } }),
          api.get("/admin/analytics/top"),
        ]);
        setData(d.data); setTs(t.data); setTop(tt.data);
      } catch { /* silent */ }
    })();
    const tick = setInterval(async () => {
      try { const d = await api.get("/admin/dashboard"); setData(d.data); } catch { /* silent */ }
    }, 20000);
    return () => clearInterval(tick);
  }, []);
  if (!data) return <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />;

  const num = (n) => Intl.NumberFormat().format(n || 0);
  const cur = (n) => `$${num(Math.round((n || 0) * 100) / 100)}`;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#FF0050] font-black">Live overview</p>
          <h1 className="text-3xl sm:text-4xl font-black font-display mt-1">Dashboard</h1>
        </div>
        <p className="text-[11px] text-zinc-500" data-testid="admin-generated-at">Mis à jour : {new Date(data.generated_at).toLocaleTimeString()}</p>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Utilisateurs" value={num(data.users.total)} sub={`${num(data.users.artists)} artistes · ${num(data.users.fans)} fans`} testid="card-users" />
        <StatCard label="Catalogue" value={num(data.catalog.tracks)} sub={`${num(data.catalog.posts)} posts`} accent="#8A2BE2" testid="card-catalog" />
        <StatCard label="Lives actifs" value={num(data.catalog.lives_active)} sub="En direct" accent="#FFD60A" testid="card-lives" />
        <StatCard label="Revenus 30j" value={cur(data.revenue.month.gross)} sub={`${num(data.revenue.month.orders)} commandes`} accent="#00F2FE" testid="card-revenue-month" />
      </section>

      <section className="grid lg:grid-cols-3 gap-3">
        <StatCard label="Revenus 24h" value={cur(data.revenue.today.gross)} sub={`${num(data.revenue.today.orders)} ventes`} accent="#22c55e" testid="card-rev-today" />
        <StatCard label="Revenus 7j" value={cur(data.revenue.week.gross)} sub={`${num(data.revenue.week.orders)} ventes`} accent="#22c55e" testid="card-rev-week" />
        <StatCard label="Cumul total" value={cur(data.revenue.lifetime.gross)} sub={`Plateforme : ${cur(data.revenue.lifetime.platform)} · Artistes : ${cur(data.revenue.lifetime.artist)}`} accent="#22c55e" testid="card-rev-lifetime" />
      </section>

      {ts && (
        <section className="rounded-2xl p-5 bg-white/[0.03] border border-white/10" data-testid="chart-revenue">
          <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3">Revenus journaliers (30 derniers jours)</h3>
          <div className="h-64" style={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={1}>
              <LineChart data={ts.revenue} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" stroke="#71717a" tick={{ fontSize: 10 }} />
                <YAxis stroke="#71717a" tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                <Line type="monotone" dataKey="gross" stroke="#FF0050" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {top && (
        <section className="grid lg:grid-cols-2 gap-3">
          <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10" data-testid="top-artists">
            <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3">Top 10 artistes (revenus)</h3>
            <div className="space-y-1.5">
              {top.artists.length === 0 ? <p className="text-zinc-500 text-sm">Aucune vente</p> :
                top.artists.map((a, i) => (
                  <button
                    key={a.id}
                    onClick={() => navigate(`/admin/artists/${a.id}`)}
                    className="w-full flex items-center gap-3 py-1.5 text-left hover:bg-white/[0.03] rounded-lg px-1 -mx-1 transition"
                    data-testid={`top-artist-${a.id}`}
                  >
                    <span className="text-zinc-500 font-bold text-xs w-6">#{i + 1}</span>
                    <span className="flex-1 text-sm truncate">{a.name}</span>
                    <span className="text-[#22c55e] font-bold text-sm tabular-nums">{cur(a.gross)}</span>
                  </button>
                ))}
            </div>
          </div>
          <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10" data-testid="top-tracks">
            <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3">Top 10 chansons</h3>
            <div className="space-y-1.5">
              {top.tracks.length === 0 ? <p className="text-zinc-500 text-sm">Aucune vente</p> :
                top.tracks.map((tk, i) => (
                  <div key={tk.id} className="flex items-center gap-3 py-1.5">
                    <span className="text-zinc-500 font-bold text-xs w-6">#{i + 1}</span>
                    <span className="flex-1 text-sm truncate">{tk.title} <span className="text-zinc-500">— {tk.artist_name}</span></span>
                    <span className="text-[#22c55e] font-bold text-sm tabular-nums">{cur(tk.gross)}</span>
                  </div>
                ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/* ---------------- Analytics ---------------- */
function AnalyticsView() {
  const [period, setPeriod] = useState("month");
  const [ts, setTs] = useState(null);
  const [top, setTop] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [t, tt] = await Promise.all([
          api.get("/admin/analytics/timeseries", { params: { period } }),
          api.get("/admin/analytics/top"),
        ]);
        setTs(t.data); setTop(tt.data);
      } catch { /* silent */ }
    })();
  }, [period]);
  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#FF0050] font-black">Insights</p>
          <h1 className="text-3xl font-black font-display mt-1">Analytics</h1>
        </div>
        <div className="flex gap-1">
          {["day", "week", "month", "year"].map((p) => (
            <button key={p} onClick={() => setPeriod(p)} data-testid={`period-${p}`} className={`px-3 py-1.5 rounded-full text-[11px] font-bold ${period === p ? "bg-white text-black" : "bg-white/5 text-zinc-400"}`}>
              {p === "day" ? "24h" : p === "week" ? "7j" : p === "month" ? "30j" : "1an"}
            </button>
          ))}
        </div>
      </header>

      {ts && (
        <div className="grid lg:grid-cols-2 gap-3">
          <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10">
            <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3">Revenus</h3>
            <div className="h-56" style={{ minWidth: 0 }}><ResponsiveContainer width="100%" height="100%" minWidth={1}>
              <BarChart data={ts.revenue}><CartesianGrid stroke="rgba(255,255,255,0.05)" /><XAxis dataKey="date" stroke="#71717a" tick={{ fontSize: 9 }} /><YAxis stroke="#71717a" tick={{ fontSize: 10 }} /><Tooltip contentStyle={{ backgroundColor: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)" }} /><Bar dataKey="gross" fill="#FF0050" /></BarChart>
            </ResponsiveContainer></div>
          </div>
          <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10">
            <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3">Inscriptions</h3>
            <div className="h-56" style={{ minWidth: 0 }}><ResponsiveContainer width="100%" height="100%" minWidth={1}>
              <BarChart data={ts.signups}><CartesianGrid stroke="rgba(255,255,255,0.05)" /><XAxis dataKey="date" stroke="#71717a" tick={{ fontSize: 9 }} /><YAxis stroke="#71717a" tick={{ fontSize: 10 }} /><Tooltip contentStyle={{ backgroundColor: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)" }} /><Bar dataKey="count" fill="#00F2FE" /></BarChart>
            </ResponsiveContainer></div>
          </div>
        </div>
      )}

      {top && (
        <div className="grid lg:grid-cols-2 gap-3">
          <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10">
            <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3">Pays (artistes)</h3>
            <div className="h-56" style={{ minWidth: 0 }}><ResponsiveContainer width="100%" height="100%" minWidth={1}>
              <PieChart><Pie dataKey="artists" data={top.countries} nameKey="country" outerRadius={80} label={(e) => e.country}>
                {top.countries.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie><Tooltip contentStyle={{ backgroundColor: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)" }} /><Legend wrapperStyle={{ fontSize: 11 }} /></PieChart>
            </ResponsiveContainer></div>
          </div>
          <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10">
            <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3">Genres</h3>
            <div className="h-56" style={{ minWidth: 0 }}><ResponsiveContainer width="100%" height="100%" minWidth={1}>
              <BarChart layout="vertical" data={top.genres}><CartesianGrid stroke="rgba(255,255,255,0.05)" /><XAxis type="number" stroke="#71717a" tick={{ fontSize: 10 }} /><YAxis type="category" dataKey="genre" stroke="#71717a" tick={{ fontSize: 10 }} width={70} /><Tooltip contentStyle={{ backgroundColor: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)" }} /><Bar dataKey="tracks" fill="#FFD60A" /></BarChart>
            </ResponsiveContainer></div>
          </div>
          <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10">
            <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3">Genre (utilisateurs)</h3>
            {/* "unspecified" always shows — honest count of who hasn't
                answered/predates the field, never hidden from the split. */}
            <div className="h-56" style={{ minWidth: 0 }}><ResponsiveContainer width="100%" height="100%" minWidth={1}>
              <PieChart><Pie dataKey="count" data={top.genders} nameKey="gender" outerRadius={80} label={(e) => GENDER_LABELS[e.gender] || e.gender}>
                {top.genders.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie><Tooltip contentStyle={{ backgroundColor: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)" }} /><Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => GENDER_LABELS[v] || v} /></PieChart>
            </ResponsiveContainer></div>
          </div>
          <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10">
            <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3">Revenus par moyen de paiement</h3>
            <div className="h-56" style={{ minWidth: 0 }}><ResponsiveContainer width="100%" height="100%" minWidth={1}>
              <PieChart><Pie dataKey="gross" data={top.providers} nameKey="provider" outerRadius={80} label={(e) => e.provider}>
                {top.providers.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie><Tooltip contentStyle={{ backgroundColor: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)" }} formatter={(v) => `$${v}`} /><Legend wrapperStyle={{ fontSize: 11 }} /></PieChart>
            </ResponsiveContainer></div>
          </div>
        </div>
      )}
    </div>
  );
}

const GENDER_LABELS = { male: "Homme", female: "Femme", unspecified: "Non renseigné" };

/* ---------------- Users ---------------- */
function UsersView({ roleFilter }) {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const isOwner = me?.role === "admin";
  const canAccounts = isOwner || (me?.permissions || []).includes("accounts");
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { q: q.trim() || undefined, role: roleFilter || undefined, limit: 200 } });
      setUsers(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, roleFilter]);

  const toggleSuspend = async (u) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { suspended: !u.suspended });
      setUsers((arr) => arr.map((x) => (x.id === u.id ? data : x)));
      toast.success(data.suspended ? "Suspendu" : "Réactivé");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const resetLink = async (u) => {
    if (!confirm(`Générer un lien de réinitialisation pour ${u.name} (${u.email}) ? Vérifie d'abord son identité.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-link`);
      try { await navigator.clipboard.writeText(data.url); } catch { /* clipboard blocked */ }
      window.prompt(`Lien valable ${data.expires_in_minutes} min, à envoyer à ${u.name} (déjà copié) :`, data.url);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-users">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#FF0050] font-black">Comptes</p>
          <h1 className="text-3xl font-black font-display mt-1">{roleFilter === "artist" ? "Artistes" : "Utilisateurs"}</h1>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Email ou nom…" className="input-base pl-9 w-72" data-testid="admin-user-search" />
        </div>
      </header>
      <div className="rounded-2xl border border-white/10 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-zinc-500 font-black bg-white/[0.02] border-b border-white/5">
          <div className="col-span-5">Compte</div>
          <div className="col-span-2">Rôle</div>
          <div className="col-span-2">Pays</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {loading ? <div className="p-6 text-center text-zinc-500"><Loader2 className="w-5 h-5 inline animate-spin" /></div> :
          users.length === 0 ? <p className="p-6 text-center text-zinc-500 text-sm">Aucun utilisateur</p> :
            users.map((u) => (
              <div
                key={u.id}
                onClick={() => u.role === "artist" && navigate(`/admin/artists/${u.id}`)}
                className={`grid grid-cols-12 gap-2 px-4 py-3 items-center text-sm hover:bg-white/[0.02] border-b border-white/5 last:border-b-0 ${u.role === "artist" ? "cursor-pointer" : ""}`}
                data-testid={`user-row-${u.id}`}
              >
                <div className="col-span-5 min-w-0">
                  <p className="font-bold truncate">{u.name} {u.suspended && <span className="ml-2 text-[10px] text-red-400 bg-red-500/10 border border-red-500/20 px-1.5 py-0.5 rounded">SUSPENDU</span>}</p>
                  <p className="text-xs text-zinc-500 truncate">{u.email}</p>
                </div>
                <div className="col-span-2 text-xs font-bold uppercase">{u.role}</div>
                <div className="col-span-2 text-xs text-zinc-400">{u.country || "—"}</div>
                <div className="col-span-3 flex gap-1.5 justify-end" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => toggleSuspend(u)} className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${u.suspended ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-amber-500/15 text-amber-400 border border-amber-500/30"}`} data-testid={`suspend-${u.id}`}>
                    {u.suspended ? "Réactiver" : "Suspendre"}
                  </button>
                  {canAccounts && (
                    <button onClick={() => resetLink(u)} className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-white/5 text-zinc-300 border border-white/10 inline-flex items-center gap-1" data-testid={`reset-link-${u.id}`}>
                      <KeyRound className="w-3 h-3" /> Lien mot de passe
                    </button>
                  )}
                  {/* No "make admin" button: there is ONE owner; responsibilities go through the Team tab, with permissions. */}
                </div>
              </div>
            ))}
      </div>
    </div>
  );
}

/* ---------------- Artist detail ---------------- */
function ArtistDetailView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/admin/artists/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [id]);

  if (data === null) return <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />;
  if (data === false) return <p className="text-zinc-500">Artiste introuvable.</p>;

  const { artist, followers, balance, tracks, revenue_timeseries } = data;
  const num = (n) => Intl.NumberFormat().format(n || 0);
  const cur = (n) => `$${num(Math.round((n || 0) * 100) / 100)}`;

  return (
    <div className="space-y-6" data-testid="admin-artist-detail">
      <button onClick={() => navigate(-1)} className="text-xs text-zinc-400 hover:text-white font-bold">← Retour</button>

      <header className="flex items-center gap-4 flex-wrap">
        {artist.avatar ? <img src={resolveImg(artist.avatar)} alt="" className="w-16 h-16 rounded-2xl object-cover" /> : <div className="w-16 h-16 rounded-2xl bg-zinc-900" />}
        <div>
          <h1 className="text-2xl sm:text-3xl font-black font-display">{artist.name}</h1>
          <p className="text-sm text-zinc-500">{artist.email} · {artist.country || "Pays non renseigné"} · Inscrit le {new Date(artist.created_at).toLocaleDateString()}</p>
        </div>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Solde disponible" value={cur(balance.available)} accent="#00F2FE" testid="artist-balance-available" />
        <StatCard label="Total gagné" value={cur(balance.lifetime_earned)} accent="#22c55e" testid="artist-balance-lifetime" />
        <StatCard label="Total retiré" value={cur(balance.lifetime_withdrawn)} accent="#8A2BE2" testid="artist-balance-withdrawn" />
        <StatCard label="Abonnés" value={num(followers)} accent="#FFD60A" testid="artist-followers" />
      </section>

      {revenue_timeseries.length > 0 && (
        <section className="rounded-2xl p-5 bg-white/[0.03] border border-white/10">
          <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3">Revenus dans le temps</h3>
          <div className="h-56" style={{ minWidth: 0 }}><ResponsiveContainer width="100%" height="100%" minWidth={1}>
            <LineChart data={revenue_timeseries} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#71717a" tick={{ fontSize: 10 }} />
              <YAxis stroke="#71717a" tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} formatter={(v) => `$${v}`} />
              <Line type="monotone" dataKey="gross" stroke="#FF0050" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer></div>
        </section>
      )}

      <section className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3 flex items-center justify-between">Morceaux <span className="text-[10px] text-zinc-500">{tracks.length}</span></h3>
        <Tbl rows={tracks} cols={[
          ["Titre", (t) => t.title],
          ["Prix", (t) => `${t.price} ${t.currency || "USD"}`],
          ["Ventes", (t) => num(t.orders)],
          ["Revenus", (t) => cur(t.gross)],
        ]} />
      </section>
    </div>
  );
}

/* ---------------- Content ---------------- */
function ContentView() {
  const [tracks, setTracks] = useState([]);
  const [projects, setProjects] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/admin/tracks", { params: { limit: 200 } }); setTracks(data); }
    catch { /* silent */ }
    try { const { data } = await api.get("/admin/releases"); setProjects(data); }
    catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);
  const askReason = (what) => {
    const reason = window.prompt(`Raison (l'artiste en sera informé) :\n${what}`, "");
    if (reason === null) return null;
    if (reason.trim().length < 5) { toast.error("Indique une raison d'au moins 5 caractères."); return null; }
    return reason.trim();
  };
  const del = async (t) => {
    const reason = askReason(`Retirer « ${t.title} » (plainte, violation de droits...)`);
    if (!reason) return;
    try {
      const { data } = await api.delete(`/admin/tracks/${t.id}`, { params: { reason } });
      toast.success(data.release_suspended ? "Morceau retiré, son projet est suspendu" : "Morceau retiré");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const suspend = async (p) => {
    const reason = askReason(`Suspendre le projet « ${p.title} ». Ses nouvelles chansons quittent la boutique ; celles publiées avant restent en vente. Ceux qui l'ont acheté le gardent.`);
    if (!reason) return;
    try { await api.post(`/admin/releases/${p.id}/suspend`, { reason }); toast.success("Projet suspendu, l'artiste est prévenu"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const reinstate = async (p) => {
    if (!window.confirm(`Rétablir « ${p.title} » ?`)) return;
    try { await api.post(`/admin/releases/${p.id}/reinstate`); toast.success("Projet rétabli"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const STATE = { draft: ["Brouillon", "text-zinc-400"], published: ["En vente", "text-emerald-400"], suspended: ["Suspendu", "text-red-400"] };
  return (
    <div className="space-y-6" data-testid="admin-content">
      <section className="space-y-3" data-testid="admin-projects">
        <header><h1 className="text-3xl font-black font-display">Projets (albums, EP, maxis)</h1><p className="text-sm text-zinc-500 mt-1">{projects.length} projet(s). On ne juge pas les œuvres : on suspend seulement après une plainte ou une violation de droits.</p></header>
        {projects.length === 0 ? <p className="text-sm text-zinc-500">Aucun projet.</p> : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {projects.map((p) => (
              <div key={p.id} className="rounded-xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid={`admin-project-${p.id}`}>
                {p.cover ? <img src={resolveImg(p.cover)} alt="" className="w-full aspect-square object-cover" /> : <div className="aspect-square bg-zinc-900" />}
                <div className="p-2">
                  <p className="text-xs font-bold truncate">{p.title}</p>
                  <p className="text-[10px] text-zinc-500 truncate">{p.artist_name} · {p.release_type.toUpperCase()} · {p.tracks_count} titres</p>
                  <p className={`text-[10px] font-black uppercase ${STATE[p.state][1]}`}>{STATE[p.state][0]}</p>
                  {p.state === "suspended" && p.suspended_reason && <p className="text-[10px] text-zinc-500 line-clamp-2">{p.suspended_reason}</p>}
                  {p.state === "suspended" ? (
                    <button onClick={() => reinstate(p)} className="mt-2 w-full text-[10px] py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 font-bold" data-testid={`reinstate-${p.id}`}>Rétablir</button>
                  ) : p.state === "published" ? (
                    <button onClick={() => suspend(p)} className="mt-2 w-full text-[10px] py-1 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20 font-bold" data-testid={`suspend-project-${p.id}`}>Suspendre</button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="space-y-3">
        <header><h2 className="text-2xl font-black font-display">Contenu musical</h2><p className="text-sm text-zinc-500 mt-1">{tracks.length} morceaux · un retrait demande une raison, et l'artiste est prévenu. Si le morceau est dans un projet publié, c'est le projet qui est suspendu.</p></header>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {tracks.map((t) => (
            <div key={t.id} className="rounded-xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid={`admin-track-${t.id}`}>
              {t.cover ? <img src={resolveImg(t.cover)} alt="" className="w-full aspect-square object-cover" /> : <div className="aspect-square bg-zinc-900" />}
              <div className="p-2">
                <p className="text-xs font-bold truncate">{t.title}</p>
                <p className="text-[10px] text-zinc-500 truncate">{t.artist_name}</p>
                <button onClick={() => del(t)} className="mt-2 w-full text-[10px] py-1 rounded bg-red-500/10 text-red-400 border border-red-500/20 font-bold">Retirer</button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ---------------- Team (owner only) ---------------- */
function TeamView() {
  const [meta, setMeta] = useState(null);
  const [staff, setStaff] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", title: "", permissions: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/staff"); setStaff(data); } catch { setStaff([]); }
  };
  useEffect(() => {
    api.get("/admin/staff/meta").then((r) => setMeta(r.data)).catch(() => {});
    load();
  }, []);

  if (!meta) return <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />;

  const toggle = (list, key) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
  const usePreset = (key) => setForm({ ...form, title: meta.presets[key].label, permissions: meta.presets[key].permissions });
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/staff", form);
      toast.success("Membre ajouté : il peut se connecter avec son e-mail et ce mot de passe.");
      setForm({ email: "", name: "", password: "", title: "", permissions: [] });
      load();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const save = async (m, patch) => {
    try { await api.patch(`/admin/staff/${m.id}`, patch); load(); }
    catch (e2) { toast.error(e2.response?.data?.detail || "Erreur"); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Retirer ${m.name} de l'équipe ? Son compte sera supprimé.`)) return;
    try { await api.delete(`/admin/staff/${m.id}`); load(); toast.success("Membre retiré"); }
    catch (e2) { toast.error(e2.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-team">
      <header>
        <h1 className="text-3xl font-black font-display">Équipe</h1>
        <p className="text-sm text-zinc-500 mt-1">Ajoute des personnes à l'administration et choisis ce que chacune peut faire. Toi seul gères l'équipe.</p>
      </header>

      <form onSubmit={add} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-3 max-w-2xl" data-testid="team-form">
        <p className="text-sm font-bold">Ajouter un membre</p>
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-zinc-500 self-center">Rôles types :</span>
          {Object.entries(meta.presets).map(([k, p]) => (
            <button key={k} type="button" onClick={() => usePreset(k)} className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10" data-testid={`team-preset-${k}`}>{p.label}</button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <input required className="input-base" placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name" />
          <input required type="email" className="input-base" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-email" />
          <input required type="text" minLength={8} className="input-base" placeholder="Mot de passe provisoire (8 car. min.)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="team-password" />
          <input className="input-base" placeholder="Fonction (ex. Comptable)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="team-title" />
        </div>
        <PermissionBoxes meta={meta} value={form.permissions} onToggle={(k) => setForm({ ...form, permissions: toggle(form.permissions, k) })} />
        <button disabled={busy || form.permissions.length === 0} className="btn-primary disabled:opacity-50" data-testid="team-add">Ajouter à l'équipe</button>
        <p className="text-[11px] text-zinc-500">Donne-lui le mot de passe provisoire de ton côté ; il pourra le changer dans son profil.</p>
      </form>

      <section className="space-y-3">
        <h2 className="text-xl font-black font-display">Membres ({(staff || []).length})</h2>
        {staff === null ? <Loader2 className="w-5 h-5 animate-spin text-zinc-500" /> : staff.length === 0 ? <p className="text-sm text-zinc-500">Personne pour le moment.</p> : staff.map((m) => (
          <div key={m.id} className="rounded-2xl border border-white/10 p-4 space-y-2" data-testid={`team-member-${m.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold">{m.name} <span className="text-zinc-500 font-normal">· {m.title || "—"} · {m.email}</span></p>
              {m.suspended && <span className="text-[10px] text-red-400 bg-red-500/10 border border-red-500/20 px-1.5 py-0.5 rounded">SUSPENDU</span>}
              <span className="flex-1" />
              <button onClick={() => save(m, { suspended: !m.suspended })} className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10">{m.suspended ? "Réactiver" : "Suspendre"}</button>
              <button onClick={() => remove(m)} className="text-xs px-3 py-1 rounded-full bg-red-500/10 text-red-300 border border-red-500/20" data-testid={`team-remove-${m.id}`}>Retirer</button>
            </div>
            <PermissionBoxes meta={meta} value={m.permissions} onToggle={(k) => { const next = toggle(m.permissions, k); if (next.length) save(m, { permissions: next }); else toast.error("Un membre garde au moins une permission."); }} />
          </div>
        ))}
      </section>
    </div>
  );
}

function PermissionBoxes({ meta, value, onToggle }) {
  return (
    <div className="grid sm:grid-cols-2 gap-1.5" data-testid="permission-boxes">
      {Object.entries(meta.permissions).map(([k, label]) => (
        <label key={k} className="flex items-start gap-2 text-xs text-zinc-300 cursor-pointer">
          <input type="checkbox" checked={value.includes(k)} onChange={() => onToggle(k)} className="mt-0.5 accent-[#FF0050]" data-testid={`perm-${k}`} />
          <span>{label}</span>
        </label>
      ))}
    </div>
  );
}

/* ---------------- Support inbox ---------------- */
function SupportAdminView() {
  const [tickets, setTickets] = useState(null);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/support/tickets", { params: { status: filter || undefined } }); setTickets(data); }
    catch { setTickets([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const open = async (id) => {
    setActive(id); setThread(null);
    try { const { data } = await api.get(`/admin/support/tickets/${id}`); setThread(data); } catch { setThread(false); }
    load();
  };
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/admin/support/tickets/${active}/messages`, { text }); setText(""); await open(active); toast.success("Réponse envoyée"); }
    catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    try { await api.patch(`/admin/support/tickets/${active}`, { status }); await open(active); }
    catch { toast.error("Erreur"); }
  };
  const unread = (tickets || []).filter((t) => t.admin_unread).length;

  return (
    <div className="space-y-4" data-testid="admin-support">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-black font-display">Support</h1>
          <p className="text-sm text-zinc-500 mt-1">{unread} message(s) à lire · {(tickets || []).length} conversation(s)</p>
        </div>
        <select value={filter} onChange={(e) => { setFilter(e.target.value); setActive(null); }} className="input-base w-44" data-testid="support-filter">
          <option value="">Toutes</option><option value="open">En attente</option><option value="answered">Répondues</option><option value="closed">Clôturées</option>
        </select>
      </header>
      <div className="grid md:grid-cols-5 gap-4">
        <div className="md:col-span-2 space-y-2">
          {tickets === null ? <Loader2 className="w-5 h-5 animate-spin text-zinc-500" /> :
            tickets.length === 0 ? <p className="text-sm text-zinc-500">Aucune conversation.</p> :
              tickets.map((t) => (
                <button key={t.id} onClick={() => open(t.id)} className={`w-full text-left rounded-xl border p-3 ${active === t.id ? "border-[#FF0050]/50 bg-[#FF0050]/5" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"}`} data-testid={`admin-ticket-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-sm truncate flex-1">{t.subject}</p>
                    {t.admin_unread && <span className="w-2.5 h-2.5 rounded-full bg-[#FF0050]" />}
                  </div>
                  <p className="text-[11px] text-zinc-500 truncate">{t.name || "—"} · {t.email}{t.user_id ? "" : " · (non connecté)"}</p>
                  <p className="text-xs text-zinc-400 mt-1 truncate">{t.last_message}</p>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{t.status}</p>
                </button>
              ))}
        </div>
        <div className="md:col-span-3">
          {!active ? <p className="text-sm text-zinc-500">Choisis une conversation.</p> :
            thread === null ? <Loader2 className="w-5 h-5 animate-spin text-zinc-500" /> :
              thread === false ? <p className="text-sm text-zinc-500">Introuvable.</p> : (
                <div className="rounded-2xl border border-white/10 p-4" data-testid="admin-thread">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-black text-lg flex-1">{thread.subject}</h2>
                    {thread.status !== "closed" ? (
                      <button onClick={() => setStatus("closed")} className="text-xs px-3 py-1.5 rounded bg-white/5 border border-white/10 font-bold">Clôturer</button>
                    ) : (
                      <button onClick={() => setStatus("open")} className="text-xs px-3 py-1.5 rounded bg-white/5 border border-white/10 font-bold">Rouvrir</button>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500">{thread.name} · {thread.email}{thread.user_id ? ` · compte ${thread.user_id}` : " · non connecté"}</p>
                  <div className="mt-4 space-y-2 max-h-[50vh] overflow-y-auto">
                    {thread.messages.map((m) => (
                      <div key={m.id} className={`max-w-[88%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.from === "admin" ? "bg-[#FF0050]/15 border border-[#FF0050]/20 ml-auto" : "bg-white/5 border border-white/10"}`}>
                        <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-0.5">{m.from === "admin" ? "Équipe" : "Utilisateur"} · {new Date(m.at).toLocaleString()}</p>
                        {m.text}
                      </div>
                    ))}
                  </div>
                  <form onSubmit={send} className="mt-4 flex gap-2">
                    <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={2000} placeholder="Ta réponse…" className="input-base flex-1" data-testid="admin-reply" />
                    <button disabled={busy} className="btn-primary px-4 disabled:opacity-60" data-testid="admin-reply-send">Envoyer</button>
                  </form>
                  <p className="text-[11px] text-zinc-600 mt-2">Compte perdu ? Vérifie l'identité, puis génère un lien depuis Utilisateurs → « Lien mot de passe ».</p>
                </div>
              )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Reports (user-generated content moderation) ---------------- */
const REASON_LABELS = {
  spam: "Spam / arnaque", harassment: "Harcèlement", hate: "Discours haineux", sexual: "Contenu sexuel",
  violence: "Violence", copyright: "Droits d'auteur", other: "Autre",
};
const TARGET_LABELS = { post: "Publication", comment: "Commentaire", track: "Morceau", user: "Utilisateur", live: "Live", submission: "Participation défi" };

function ReportsView() {
  const [reports, setReports] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/reports"); setReports(data); }
    catch { setReports([]); }
  };
  useEffect(() => { load(); }, []);
  const setStatus = async (r, status) => {
    try { await api.patch(`/admin/reports/${r.id}`, { status }); load(); }
    catch { toast.error("Erreur"); }
  };
  const takeDown = async (r) => {
    if (!confirm("Supprimer définitivement ce contenu ?")) return;
    try { await api.delete(`/admin/content/${r.target_type}/${r.target_id}`); toast.success("Contenu supprimé"); load(); }
    catch { toast.error("Impossible de supprimer"); }
  };
  if (!reports) return <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />;
  const open = reports.filter((r) => r.status === "open").length;
  return (
    <div className="space-y-4" data-testid="admin-reports">
      <header>
        <h1 className="text-3xl font-black font-display">Signalements</h1>
        <p className="text-sm text-zinc-500 mt-1">{open} à traiter · {reports.length} au total</p>
      </header>
      {reports.length === 0 && <p className="text-zinc-500 text-sm">Aucun signalement.</p>}
      <div className="space-y-2">
        {reports.map((r) => (
          <div key={r.id} className={`rounded-xl border p-3 ${r.status === "open" ? "bg-amber-500/5 border-amber-500/20" : "bg-white/[0.02] border-white/10 opacity-70"}`} data-testid={`report-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="chip bg-white/10 text-white">{TARGET_LABELS[r.target_type] || r.target_type}</span>
              <span className="chip bg-red-500/15 text-red-300">{REASON_LABELS[r.reason] || r.reason}</span>
              <span className="text-zinc-500">par {r.reporter_name} · {new Date(r.created_at).toLocaleString()}</span>
              <span className="ml-auto text-zinc-400 font-bold uppercase">{r.status}</span>
            </div>
            {r.details && <p className="mt-2 text-sm text-zinc-300">{r.details}</p>}
            <p className="mt-1 text-[11px] text-zinc-600 break-all">id : {r.target_id}</p>
            {r.status === "open" && (
              <div className="mt-3 flex flex-wrap gap-2">
                {(r.target_type === "post" || r.target_type === "comment" || r.target_type === "submission") && (
                  <button onClick={() => takeDown(r)} className="text-xs px-3 py-1.5 rounded bg-red-500/10 text-red-400 border border-red-500/20 font-bold">Supprimer le contenu</button>
                )}
                <button onClick={() => setStatus(r, "resolved")} className="text-xs px-3 py-1.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 font-bold">Traité</button>
                <button onClick={() => setStatus(r, "dismissed")} className="text-xs px-3 py-1.5 rounded bg-white/5 text-zinc-300 border border-white/10 font-bold">Ignorer</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Financial ---------------- */
function FinancialView() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/financial/transactions").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />;
  const cur = (n, c = "USD") => `${(n || 0).toFixed(2)} ${(c || "USD").toUpperCase()}`;
  const dl = async (kind) => {
    // HIGH-SEVERITY SECURITY FIX (S5): `window.open` sent no Authorization
    // header AND leaked the URL through browser history. We now stream the
    // export via authenticated fetch, then trigger a client-side blob save.
    try {
      const res = await api.get(`/admin/financial/export.csv?kind=${encodeURIComponent(kind)}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `metazik_${kind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Export échoué — vérifie que tu es bien admin.");
    }
  };
  return (
    <div className="space-y-6" data-testid="admin-financial">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <h1 className="text-3xl font-black font-display">Finance</h1>
        <div className="flex gap-2">
          <button onClick={() => dl("purchases")} className="btn-secondary inline-flex items-center gap-2 text-xs" data-testid="export-purchases"><Download className="w-3.5 h-3.5" /> CSV Ventes</button>
          <button onClick={() => dl("sessions")} className="btn-secondary inline-flex items-center gap-2 text-xs" data-testid="export-sessions"><Download className="w-3.5 h-3.5" /> CSV Stripe</button>
          <button onClick={() => dl("withdrawals")} className="btn-secondary inline-flex items-center gap-2 text-xs" data-testid="export-withdrawals"><Download className="w-3.5 h-3.5" /> CSV Retraits</button>
        </div>
      </header>

      <PayoutsAdmin />

      <Section title="Achats (purchases)" count={data.purchases.length} testid="purchases-list">
        <Tbl rows={data.purchases.slice(0, 30)} cols={[
          ["Date", (r) => new Date(r.created_at).toLocaleString()],
          ["Montant", (r) => cur(r.amount, r.currency)],
          ["Méthode", (r) => r.method],
          ["Statut", (r) => r.status],
          ["Plateforme", (r) => cur(r.platform_cut, r.currency)],
          ["Artiste", (r) => cur(r.artist_cut, r.currency)],
        ]} />
      </Section>

      <Section title="Sessions Stripe" count={data.stripe_sessions.length} testid="stripe-list">
        <Tbl rows={data.stripe_sessions.slice(0, 30)} cols={[
          ["Date", (r) => new Date(r.created_at).toLocaleString()],
          ["Session", (r) => (r.session_id || "").slice(0, 14) + "…"],
          ["Montant", (r) => cur(r.amount, r.currency)],
          ["Paiement", (r) => r.payment_status],
          ["Statut", (r) => r.status],
        ]} />
      </Section>

      <Section title="Retraits" count={data.withdrawals.length} testid="withdrawals-list">
        <Tbl rows={data.withdrawals.slice(0, 30)} cols={[
          ["Date", (r) => new Date(r.created_at).toLocaleString()],
          ["Notes", (r) => Intl.NumberFormat().format(r.notes_amount || 0)],
          ["FCFA", (r) => Intl.NumberFormat().format(r.fcfa_amount || 0)],
          ["Méthode", (r) => r.method],
          ["Statut", (r) => r.status],
        ]} />
      </Section>
    </div>
  );
}

function Section({ title, count, testid, children }) {
  return (
    <section className="rounded-2xl bg-white/[0.03] border border-white/10 p-5" data-testid={testid}>
      <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3 flex items-center justify-between">{title} <span className="text-[10px] text-zinc-500">{count}</span></h3>
      {children}
    </section>
  );
}

function Tbl({ rows, cols }) {
  if (rows.length === 0) return <p className="text-zinc-500 text-sm text-center py-6">Aucune entrée</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-zinc-500 uppercase tracking-wider text-[10px] font-black">{cols.map((c) => <th key={c[0]} className="text-left py-2 pr-4">{c[0]}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-t border-white/5">
            {cols.map((c) => <td key={c[0]} className="py-2 pr-4 text-zinc-300">{c[1](r)}</td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

/* ---------------- Security ---------------- */
function SecurityView() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/security/recent").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />;
  return (
    <div className="space-y-6" data-testid="admin-security">
      <h1 className="text-3xl font-black font-display">Sécurité</h1>
      <div className="grid md:grid-cols-3 gap-3">
        <StatCard label="Comptes suspendus" value={d.suspended} accent="#ef4444" testid="sec-suspended" />
        <StatCard label="Inscriptions récentes" value={d.recent_signups.length} accent="#00F2FE" testid="sec-signups" />
        <StatCard label="2FA" value="—" sub="Bientôt disponible" accent="#FFD60A" testid="sec-2fa" />
      </div>
      <section className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3 flex items-center gap-2"><Activity className="w-3.5 h-3.5" /> Dernières inscriptions</h3>
        <Tbl rows={d.recent_signups} cols={[
          ["Date", (r) => new Date(r.created_at).toLocaleString()],
          ["Nom", (r) => r.name],
          ["Email", (r) => r.email],
          ["Rôle", (r) => r.role],
        ]} />
      </section>
    </div>
  );
}
